import { Badge } from "../../../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { TableCell, TableRow } from "../../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState } from "react";
import EngagementHistoryModal from "../../modals/engagementHistoryModal";
import { useTranslation } from "react-i18next";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { CheckCircle, Clock } from "lucide-react";
import useAuth from "../../../../hooks/useAuth";
import UnfinishedEngagementModal from "../../modals/unfinishedEngagementModal";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useProcess from "../../../../hooks/useProcess";
import { config } from "../../../../Environment";
import { useLocation, useNavigate } from "react-router-dom";

const getStatusIcon = (status: string) => {
  switch (status) {
    case "FINISHED":
      return <CheckCircle className="h-3 w-3 text-green-500" />;
    default:
      return <Clock className="h-3 w-3 text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "FINISHED":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "RUNNING":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

const SingleActivity = ({
  prescription,
  prescriptions,
  setPrescriptions,
  index,
  editPrescription,
  viewPrescription,
  renamePrescription,
  deletePrescription,
  copyPrescriptionAsExample,
  sharePrescription,
  managePermissions,
  exportPrescription,
  runPrescription,
  refreshLists,
  isMobile,
}: any) => {
  const { auth } = useAuth();

  const [unfinishedEngagementModal, setUnfinishedEngagementModal] =
    useState(false);
  const [engagementHistoryModal, setEngagementHistoryModal] = useState(false);

  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { startProcess } = useProcess();
  const navigate = useNavigate();
  const location = useLocation();

  const handleStartNew = async () => {
    const prescriptionId = prescription._id;
    await startProcess({
      prescriptionId,
      onError: () => {
        toast.error("An error occurred while starting the prescription.");
      },
    });
  };

  const runningProcessCount = useMemo(() => {
    return prescription.processes.filter(
      (process: any) => process.status === "RUNNING"
    ).length;
  }, [prescription.processes]);

  const handleNameClick = (prescription: any) => {
    if (runningProcessCount > 0) {
      setUnfinishedEngagementModal(true);
    } else {
      handleStartNew();
    }
  };

  return (
    <>
      <TableRow className="max-w-full">
        <TableCell
          className={`${
            !prescription.valid ? "text-gray-300" : ""
          } border-none max-w-72 sm:max-w-lg md:max-w-auto`}
        >
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleNameClick(prescription)}
              className="text-left  text-blue-600 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium truncate md:max-w-auto"
            >
              {prescription.name}
            </button>
            {runningProcessCount > 0 && (
              <div className="flex items-center">
                <Badge
                  variant="outline"
                  className={`text-xs md:space-x-1 ${getStatusColor(
                    "RUNNING"
                  )}`}
                >
                  <span className="hidden md:block">
                    {getStatusIcon("RUNNING")}
                  </span>
                  <span className="whitespace-nowrap">
                    {runningProcessCount}{" "}
                    <span className="hidden md:inline">{t("in_progress")}</span>
                  </span>
                </Badge>
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className="border-none">
          <DropdownMenu>
            <DropdownMenuTrigger className="bg-transparent ms-2">
              <div className="w-[30px]">
                <FontAwesomeIcon
                  title={t("actions")}
                  style={{ paddingTop: "4px" }}
                  icon={solid("ellipsis-vertical")}
                />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {!!prescription.valid ? (
                <>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={
                      !prescription.valid
                        ? () => {}
                        : () => runPrescription(prescriptions, index)
                    }
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("run")}
                        icon={solid("play")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("run")}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : (
                <></>
              )}
              {auth.email && !!prescription.valid ? (
                <DropdownMenuItem
                  onClick={() => setEngagementHistoryModal(true)}
                >
                  <FontAwesomeIcon
                    title={t("export")}
                    icon={solid("eye")}
                    className="ml-2 mr-2 mt-1 w-[15px]"
                  />
                  {t("engagement_history")}
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              {auth.email && !!prescription.valid ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={
                    !auth.email || !prescription.valid
                      ? () => {}
                      : () => sharePrescription(index)
                  }
                >
                  <div className="flex">
                    <FontAwesomeIcon
                      title={t("share")}
                      icon={solid("share-nodes")}
                      className="ml-2 mr-2 mt-1 w-[15px]"
                    />
                    <span>{t("share")}</span>
                  </div>
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              {auth.email ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={
                    !auth.email ? () => {} : () => deletePrescription(index)
                  }
                >
                  {prescription.versions > 0 ? (
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("archive")}
                        icon={solid("box-archive")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("archive")}</span>
                    </div>
                  ) : (
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("delete")}
                        icon={solid("circle-xmark")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("delete")}</span>
                    </div>
                  )}
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              {!isMobile && (
                <>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => editPrescription(index)}
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("edit")}
                        icon={solid("pen")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("edit")}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => viewPrescription(index, true)}
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("view")}
                        icon={solid("eye")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("view")}</span>
                    </div>
                  </DropdownMenuItem>
                </>
              )}
              {auth.email && !isMobile ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={
                    !auth.email ? () => {} : () => renamePrescription(index)
                  }
                >
                  <div className="flex">
                    <FontAwesomeIcon
                      title={t("rename")}
                      icon={solid("file-signature")}
                      className="ml-2 mr-2 mt-1 w-[15px]"
                    />
                    <span>{t("rename")}</span>
                  </div>
                </DropdownMenuItem>
              ) : (
                <></>
              )}

              {auth.email && !!prescription.valid && !isMobile ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={
                      !auth.email
                        ? () => {}
                        : () => copyPrescriptionAsExample(index)
                    }
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("copy_activity_as_example")}
                        icon={solid("upload")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("copy_activity_as_example")}</span>
                    </div>
                  </DropdownMenuItem>
                </>
              ) : (
                <></>
              )}

              {auth.email && !!prescription.valid && !isMobile ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={
                    !auth.email || !prescription.valid
                      ? () => {}
                      : () => managePermissions(index)
                  }
                >
                  <div className="flex">
                    <FontAwesomeIcon
                      title={t("manage_permissions")}
                      icon={solid("lock")}
                      className="ml-2 mr-2 mt-1 w-[15px]"
                    />
                    <span>{t("manage_permissions")}</span>
                  </div>
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              {auth.email && !!prescription.valid && !isMobile ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={
                    !auth.email ? () => {} : () => exportPrescription(index)
                  }
                >
                  <div className="flex">
                    <FontAwesomeIcon
                      title={t("export")}
                      icon={solid("download")}
                      className="ml-2 mr-2 mt-1 w-[15px]"
                    />
                    <span>{t("export")}</span>
                  </div>
                </DropdownMenuItem>
              ) : (
                <></>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {/* Unfinished Process Modal */}
      {engagementHistoryModal && (
        <EngagementHistoryModal
          engagementHistoryModal={engagementHistoryModal}
          setEngagementHistoryModal={setEngagementHistoryModal}
          prescription={prescription}
          refreshLists={refreshLists}
          setPrescriptions={setPrescriptions}
        />
      )}

      {/* History Modal */}
      {unfinishedEngagementModal && (
        <UnfinishedEngagementModal
          unfinishedEngagementModal={unfinishedEngagementModal}
          setUnfinishedEngagementModal={setUnfinishedEngagementModal}
          prescription={prescription}
          setPrescriptions={setPrescriptions}
          refreshLists={refreshLists}
        />
      )}
    </>
  );
};

export default SingleActivity;
